import * as React from "react";

import { decimalToThreePlaces } from "@eave/shared/utils/numbers";
import { EMDASH } from "@eave/shared/utils/general";

import {
  AppraisalKinds,
  ArchiveKinds,
  CloneKinds,
  ConditionKinds,
  DocmagicKinds,
  FeeChangeKinds,
  PointsAndCreditsKinds,
  SSNKinds,
  SandboxKinds,
  TRUSEventShape,
  FieldUpdateKinds,
} from "web_modules/Events/types";
import moment from "moment-timezone";

import {
  isAppraisalKind,
  isArchivedKind,
  isCloneKind,
  isConditionKind,
  isDeclarationsKind,
  isDocmagicKind,
  isFeeChangeKind,
  isHurdleNoteKind,
  isLobLetterKind,
  isPointsAndCreditsKind,
  isSSNKind,
  isSandboxKind,
  isWorksheetKind,
  isFieldUpdateKind,
} from "../shared";

import { Fields } from "./Fields";

const humanize = require("underscore.string/humanize");

const styles = require("../styles.module.scss");

const dollarFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface Props {
  event: TRUSEventShape;
  expanded: boolean;
}

const DefaultDescription: React.FC<Props> = ({ event, expanded }) => {
  if (!event.fields) {
    return <span className={styles.ellipsis}>—</span>;
  }
  if (expanded) {
    return <Fields fields={event.fields} />;
  }
  return <span className={styles.ellipsis}>More details…</span>;
};

const AppraisalEventDescription: React.FC<Props> = ({ event, expanded }) => {
  const map = {
    [AppraisalKinds.ReportSent]:
      "Appraisal Report sent to borrower via 'Your Documents' section of secure online dashboard",
    [AppraisalKinds.ReportDelivered]:
      "Appraisal Report delivered to borrower via 'Your Documents' section of secure online dashboard",
    [AppraisalKinds.ReportRequested]: `Appraisal Report requested date entered as ${moment(
      event.fields["Requested Date"],
    ).format("MM/DD/YYYY")}`,
    [AppraisalKinds.ReportExpected]: `Appraisal Report expected date entered as ${moment(
      event.fields["Expected Date"],
    ).format("MM/DD/YYYY")}`,
    [AppraisalKinds.ReportApproved]: `Appraisal Report approved date entered as ${moment(
      event.fields["Approved Date"],
    ).format("MM/DD/YYYY")}`,
  };

  const desc = map[event.kind];
  if (desc) {
    return (
      <>
        {desc}
        {expanded && event.fields && (
          <div className={styles.fieldsMargins}>
            <Fields fields={event.fields} />
          </div>
        )}
      </>
    );
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const ArchivedDescription: React.FC<Props> = ({ event, expanded }) => {
  if (event.kind === ArchiveKinds.LoanArchived) {
    return <>Loan was archived</>;
  }
  if (event.kind === ArchiveKinds.LoanUnArchived) {
    return <>Loan was un-archived</>;
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const CloneDescription: React.FC<Props> = ({ event, expanded }) => {
  const { kind, fields } = event;

  if (kind === CloneKinds.Clone) {
    return (
      <>
        Cloned from:{" "}
        <a href={`${window.eaveEnv.apps.operator}/underwriter/app/${fields.cloned_from}/notes`}>
          {fields.cloned_from}
        </a>{" "}
        as of {moment(fields.cloned_as_of).tz(moment.tz.guess()).format("MM/DD/YY HH:mm z")}
      </>
    );
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const ConditionDescription: React.FC<Props> = ({ event, expanded }) => {
  const { kind, fields } = event;

  if (kind === ConditionKinds.Update) {
    return (
      <>
        Status of "{fields.condition}" updated to "{humanize(fields.current_status)}" (was "
        {humanize(fields.previous_status)}")
      </>
    );
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const DeclarationsEventDescription: React.FC<Props> = ({ event, expanded }) => (
  <>
    {`Declarations modified for borrower ${event.fields.BorrowerEntity}`}
    {expanded && (
      <div className={styles.fieldsMargins}>
        <pre>
          <code>{event.fields && event.fields.Diff}</code>
        </pre>
      </div>
    )}
  </>
);

const DocmagicEventDescription: React.FC<Props> = ({ event, expanded }) => {
  const map = {
    [DocmagicKinds.CompleteClicksign]: "Complete Clicksign",
    [DocmagicKinds.CompleteEsign]: "Complete Esign",
    [DocmagicKinds.CompleteSigreq]: "Complete Sigreq",
    [DocmagicKinds.ConsentReapplied]: "Consent Reapplied",
    [DocmagicKinds.ConsentWithdraw]: "Consent Withdraw",
    [DocmagicKinds.CreateSigreq]: "Create Sigreq",
    [DocmagicKinds.DeliverDocument]: "Deliver Document",
    [DocmagicKinds.DeliverPaperCopy]: "Deliver Paper Copy",
    [DocmagicKinds.DeliverReport]: "Deliver Report",
    [DocmagicKinds.ExpireSigreq]: "Expire Sigreq",
    [DocmagicKinds.InksignFaxReceived]: "Inksign Fax Received",
    [DocmagicKinds.InksignUploadReceived]: "Inksign Upload Received",
    [DocmagicKinds.NoteToSender]: "Note To Sender",
    [DocmagicKinds.ReminderIncomplete]: "Reminder Incomplete",
    [DocmagicKinds.RespaComplete]: "Respa Complete",
    [DocmagicKinds.SendSigreqInvite]: "Send Sigreq Invite",
    [DocmagicKinds.StartEsign]: "Start Esign",
    [DocmagicKinds.ViewSigreq]: "View Sigreq",
  };

  const desc = map[event.kind];
  if (desc) {
    return (
      <>
        {desc}
        {expanded && event.fields && (
          <div className={styles.fieldsMargins}>
            <Fields fields={event.fields} />
          </div>
        )}
      </>
    );
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const FeeChangeDescription: React.FC<Props> = ({ event, expanded }) => {
  const map = {
    [FeeChangeKinds.LoanOrigination]: "Loan origination fee",
  };

  if (!map[event.kind]) {
    return <DefaultDescription event={event} expanded={expanded} />;
  }

  const changeTypeDescriptionEnum = {
    Create: "added",
    Update: "modified",
    Delete: "removed",
  };

  const { actual_amount, estimated_amount, change_type } = event.fields;

  const prefix = `${map[event.kind]} ${changeTypeDescriptionEnum[change_type]}`;
  if (!expanded) {
    return <>{prefix}…</>;
  }

  const formatFee = amount => {
    if (!amount) {
      return;
    }
    return amount === "0" ? "0" : dollarFormatter.format(Number(amount));
  };

  const humanReadableFields = {
    "Actual amount": formatFee(actual_amount) ?? EMDASH,
    "Estimated amount": formatFee(estimated_amount) ?? EMDASH,
    "Change type": change_type ?? EMDASH,
  };

  return (
    <>
      {prefix}
      <div className={styles.fieldsMargins}>
        <Fields fields={humanReadableFields} />
      </div>
    </>
  );
};

const HurdleNoteDescription: React.FC<Props> = ({ event: { fields } }) => {
  return <>{fields && fields.note}</>;
};

const LobLetterDescription: React.FC<Props> = ({ event, expanded }) => {
  const map = {
    lob_letter_created: "Created",
    lob_letter_deleted: "Deleted",
    lob_letter_in_local_area: "In local area",
    lob_letter_in_transit: "In transit",
    lob_letter_mailed: "Mailed",
    lob_letter_processed_for_delivery: "Processed for delivery",
    lob_letter_rendered_pdf: "Rendered PDF",
    lob_letter_rendered_thumbnails: "Rendered thumbnails",
    lob_letter_rerouted: "Rerouted",
    lob_letter_returned_to_sender: "Returned to sender",
  };

  const desc = map[event.kind];
  if (desc) {
    return <>{desc}</>;
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const PointsAndCreditsDescription: React.FC<Props> = ({ event, expanded }) => {
  if (event.kind !== PointsAndCreditsKinds.RateSelection) {
    return <DefaultDescription event={event} expanded={expanded} />;
  }

  const prefix = `Rate stack chosen`;
  if (!expanded) {
    return <>{prefix}…</>;
  }

  const { displayed_loan_rate, daily_base_rate, price_delta, amount, points_or_credits } =
    event.fields;

  const humanizedFields = {
    "Displayed loan rate": `${decimalToThreePlaces(displayed_loan_rate)}%`,
    "Base rate": `${decimalToThreePlaces(daily_base_rate)}%`,
    "Price delta": price_delta === "0" ? "0" : `${decimalToThreePlaces(price_delta)}%`,
    Amount: amount === "0" ? "0" : dollarFormatter.format(Number(amount)),
    "Points/credits": humanize(points_or_credits),
  };

  return (
    <>
      {prefix}:
      <div className={styles.fieldsMargins}>
        <Fields fields={humanizedFields} />
      </div>
    </>
  );
};

const SandboxEventDescription: React.FC<Props> = ({ event, expanded }) => {
  if (event.kind !== SandboxKinds.ReworkRequest) {
    return <DefaultDescription event={event} expanded={expanded} />;
  }

  const prefix = `Rework request submitted`;
  if (!expanded) {
    return <>{prefix}…</>;
  }

  const humanizedFields = {
    Description: event.fields.description,
    "Raw inputs": JSON.stringify(JSON.parse(event.fields.sandbox_request), null, "  "),
  };

  return (
    <>
      {prefix}:
      <div className={styles.fieldsMargins}>
        <Fields fields={humanizedFields} />
      </div>
    </>
  );
};

const SSNEventDescription: React.FC<Props> = ({ event, expanded }) => {
  if (event.kind === SSNKinds.Update) {
    const humanReadableBorrower = {
      1: "borrower 1",
      2: "borrower 2",
      3: "both borrowers",
    }[event.fields.borrower];

    return <div>{`SSN updated for ${humanReadableBorrower}`}</div>;
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const FieldUpdateDescription: React.FC<Props> = ({ event, expanded }) => {
  if (event.kind === FieldUpdateKinds.FieldUpdateExpectedGrossMonthlyRentalIncome) {
    return (
      <div>
        {`Expected Gross Monthly Rental Income was updated from ${dollarFormatter.format(
          Number(event.fields.original_value),
        )} to ${dollarFormatter.format(Number(event.fields.new_value))}.`}
      </div>
    );
  }

  return <DefaultDescription event={event} expanded={expanded} />;
};

const Description: React.FC<Props> = props => {
  const { kind } = props.event;

  if (isAppraisalKind(kind)) {
    return <AppraisalEventDescription {...props} />;
  }
  if (isArchivedKind(kind)) {
    return <ArchivedDescription {...props} />;
  }
  if (isCloneKind(kind)) {
    return <CloneDescription {...props} />;
  }
  if (isConditionKind(kind)) {
    return <ConditionDescription {...props} />;
  }
  if (isDeclarationsKind(kind)) {
    return <DeclarationsEventDescription {...props} />;
  }
  if (isDocmagicKind(kind)) {
    return <DocmagicEventDescription {...props} />;
  }
  if (isFeeChangeKind(kind)) {
    return <FeeChangeDescription {...props} />;
  }
  if (isHurdleNoteKind(kind)) {
    return <HurdleNoteDescription {...props} />;
  }
  if (isLobLetterKind(kind)) {
    return <LobLetterDescription {...props} />;
  }
  if (isPointsAndCreditsKind(kind)) {
    return <PointsAndCreditsDescription {...props} />;
  }
  if (isSandboxKind(kind)) {
    return <SandboxEventDescription {...props} />;
  }
  if (isSSNKind(kind)) {
    return <SSNEventDescription {...props} />;
  }
  if (isWorksheetKind(kind)) {
    // fall through to DefaultDescription
  }
  if (isFieldUpdateKind(kind)) {
    return <FieldUpdateDescription {...props} />;
  }

  return <DefaultDescription {...props} />;
};

export default Description;
